import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardTable from '../components/DashboardTable';
import UserModal from '../components/UserModal';
import EventModal from '../components/EventModal';
import AccountsModal from '../components/AccountsModal';
import ReceiptModal from '../components/ReceiptModal';
import CustomersModal from '../components/CustomersModal';
import ConfigModal from '../components/ConfigModal';
import EventCustomerModal from '../components/EventCustomerModal';
import TicketsModal from '../components/TicketsModal';
import TaskModal from '../components/TaskModal';
import BookTicketsForm from '../components/BookTicketsForm';
import AlertBox from '../components/AlertBox';
import { usersAPI, eventsAPI, customersAPI, transactionsAPI, eventCustomersAPI, ticketsAPI, configAPI, artistsAPI, tasksAPI } from '../services/api';
import Papa from 'papaparse';
import { API_BASE_URL } from '../config';
import { formatDateEST, formatDateTimeEST } from '../utils/dateUtils';

type ContextType = 'users' | 'events' | 'customers' | 'accounts' | 'event-customers' | 'config' | 'book-tickets' | 'artist-voting' | 'tasks';

interface CsvRow {
  name: string;
  email: string;
  mode: string;
  adults: string;
  kids: string;
  confirmed: string;
}



const Dashboard: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [alert, setAlert] = useState<{ type: 'error' | 'success' | 'info', message: string } | null>(null);
  const [context, setContext] = useState<ContextType>('users');
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [editUserUuid, setEditUserUuid] = useState<string | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [events, setEvents] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [editEventUuid, setEditEventUuid] = useState<string | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editCustomerUuid, setEditCustomerUuid] = useState<string | null>(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [showAccountsModal, setShowAccountsModal] = useState(false);
  const [editTransaction, setEditTransaction] = useState<any | null>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptTransaction, setReceiptTransaction] = useState<any | null>(null);
  const [selectedEventUuid, setSelectedEventUuid] = useState<string>('');
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [eventsForDropdown, setEventsForDropdown] = useState<any[]>([]);
  const [eventCustomers, setEventCustomers] = useState<any[]>([]);
  const [sendingTickets, setSendingTickets] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [configs, setConfigs] = useState<any[]>([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [editConfigUuid, setEditConfigUuid] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showEventCustomerModal, setShowEventCustomerModal] = useState(false);
  const [editEventCustomerUuid, setEditEventCustomerUuid] = useState<string | null>(null);
  const [showTicketsModal, setShowTicketsModal] = useState(false);
  const [selectedTicketsCustomerUuid, setSelectedTicketsCustomerUuid] = useState<string | null>(null);
  const [selectedTicketsCustomerName, setSelectedTicketsCustomerName] = useState<string>('');
  
  // Artist voting states
  const [artists, setArtists] = useState<any[]>([]);
  const [years, setYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedArtist, setSelectedArtist] = useState<any | null>(null);
  const [currentVote, setCurrentVote] = useState<boolean | null>(null);
  const [voteLoading, setVoteLoading] = useState(false);
  const [sendingDecision, setSendingDecision] = useState<number | null>(null);
  const [showVoteModal, setShowVoteModal] = useState(false);

  // Task management states
  const [tasks, setTasks] = useState<any[]>([]);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editTaskUuid, setEditTaskUuid] = useState<string | null>(null);
  const [taskStats, setTaskStats] = useState<any>(null);
  const [taskStatusFilter, setTaskStatusFilter] = useState<string>('');
  const [taskAssignedFilter, setTaskAssignedFilter] = useState<string>('');

  const handleCsvUpload = async (file: File, eventId: number) => {
    const REQUIRED_COLUMNS = ['name', 'email', 'mode', 'adults', 'kids', 'confirmed'];
    
    Papa.parse<CsvRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results: Papa.ParseResult<CsvRow>) => {
        const { data, errors, meta } = results;
        
        if (errors.length > 0) {
          setAlert({ type: 'error', message: 'CSV parsing error: ' + errors[0].message });
          return;
        }
        
        // Validate columns
        const columns = meta.fields || [];
        const missing = REQUIRED_COLUMNS.filter(col => !columns.includes(col));
        if (missing.length > 0) {
          setAlert({ type: 'error', message: 'Missing required columns: ' + missing.join(', ') });
          return;
        }
        
        // Filter valid rows
        const validRows = data.filter((row: CsvRow) => row.name && row.email);
        if (validRows.length === 0) {
          setAlert({ type: 'error', message: 'No valid rows found in CSV.' });
          return;
        }
        
        // Send to backend
        try {
          setLoading(true);
          const response = await fetch(`${API_BASE_URL}/csv-upload`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              event_id: eventId,
              rows: validRows
            })
          });
          
          if (!response.ok) {
            throw new Error('Failed to upload CSV');
          }
          
          const result = await response.json();
          setAlert({ type: 'success', message: `Successfully uploaded! Added ${result.successCount} customers. ${result.errorCount} errors.` });
          
          // Refresh events if needed
          if (context === 'events') {
            eventsAPI.getAll({ search, page: currentPage }).then(res => {
              setEvents(res.data.events || []);
              setTotalPages(res.data.totalPages || 1);
            });
          }
        } catch (error) {
          setAlert({ type: 'error', message: 'Failed to upload CSV. Please try again.' });
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleSendTickets = async (eventCustomerUuid: string) => {
    try {
      setSendingTickets(eventCustomerUuid);
      const response = await ticketsAPI.generate(eventCustomerUuid);
      setAlert({ type: 'success', message: response.data.message });
      
      // Refresh event customers
      if (selectedEventId) {
        eventCustomersAPI.getByEventId(selectedEventId, { search, page: currentPage }).then(res => {
          setEventCustomers(res.data.eventCustomers || []);
          setTotalPages(res.data.totalPages || 1);
        });
      }
    } catch (error: any) {
      setAlert({ type: 'error', message: error.response?.data?.error || 'Failed to send tickets' });
    } finally {
      setSendingTickets(null);
    }
  };

  const exportEventCustomersCsv = () => {
    if (!eventCustomers.length) return;
    const eventTitle = eventCustomers[0]?.event_title || 'event';
    const rows = eventCustomers.map((ec: any) => ({
      'Name': ec.customer_name || '',
      'Email': ec.email || '',
      'Mode': ec.mode || '',
      'Adults': ec.number_of_tickets_adults ?? '',
      'Kids': ec.number_of_tickets_kids ?? '',
      'Amount': ec.amount ?? '',
      'Confirmed': ec.confirmed ? 'Yes' : 'No',
      'Ticket Emailed': ec.ticket_emailed ? 'Yes' : 'No',
      'Admitted': ec.admitted ? 'Yes' : 'No',
    }));
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${eventTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_customers.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCustomerNameClick = (row: any) => {
    setSelectedTicketsCustomerUuid(row.uuid);
    setSelectedTicketsCustomerName(row.customer_name);
    setShowTicketsModal(true);
  };

  // Artist voting handlers
  const openVoteModal = async (artist: any) => {
    setSelectedArtist(artist);
    setShowVoteModal(true);
    
    // Fetch current user's vote
    try {
      const response = await artistsAPI.getMyVote(artist.id);
      setCurrentVote(response.data.voted ? response.data.vote : null);
    } catch (error) {
      console.error('Error fetching vote:', error);
      setCurrentVote(null);
    }
  };

  const handleVote = async (artistId: number, vote: boolean) => {
    try {
      setVoteLoading(true);
      await artistsAPI.voteOnArtist(artistId, vote);
      setAlert({ type: 'success', message: 'Vote recorded successfully' });
      
      // Refresh artists list
      if (selectedYear) {
        const res = await artistsAPI.getArtists(selectedYear);
        setArtists(res.data.artists || []);
      }
      
      // Close modal
      setShowVoteModal(false);
      setSelectedArtist(null);
      setCurrentVote(null);
    } catch (error: any) {
      setAlert({ type: 'error', message: 'Failed to record vote' });
    } finally {
      setVoteLoading(false);
    }
  };

  const handleSendDecision = async (artistId: number) => {
    if (!window.confirm('Are you sure you want to send the decision email to this artist? This action cannot be undone.')) {
      return;
    }

    try {
      setSendingDecision(artistId);
      const response = await artistsAPI.sendDecision(artistId);
      setAlert({ type: 'success', message: `Decision email sent successfully! The artist has been ${response.data.decision}.` });
      
      // Refresh artists list
      if (selectedYear) {
        const res = await artistsAPI.getArtists(selectedYear);
        setArtists(res.data.artists || []);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to send decision email';
      setAlert({ type: 'error', message: errorMessage });
    } finally {
      setSendingDecision(null);
    }
  };

  const handleDeleteTask = async (uuid: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      await tasksAPI.delete(uuid);
      setAlert({ type: 'success', message: 'Task deleted successfully' });
      // Refresh tasks list
      if (user && user.role === 'admin') {
        const res = await tasksAPI.getAll({ 
          search, 
          page: currentPage, 
          status: taskStatusFilter,
          assignedTo: taskAssignedFilter 
        });
        setTasks(res.data.tasks || []);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (error: any) {
      setAlert({ type: 'error', message: 'Failed to delete task' });
    }
  };

  const filteredData =
    context === 'users'
      ? users
      : context === 'events'
        ? events
        : context === 'customers'
          ? customers
          : context === 'accounts'
            ? transactions
            : context === 'event-customers'
              ? eventCustomers
              : context === 'config'
                ? configs
                : context === 'tasks'
                  ? tasks
                  : [];

  // Read context from URL parameter on mount
  useEffect(() => {
    window.scrollTo(0, 0);
    const contextParam = searchParams.get('context');
    if (contextParam && ['users', 'events', 'customers', 'accounts', 'event-customers', 'config', 'book-tickets', 'artist-voting', 'tasks'].includes(contextParam)) {
      setContext(contextParam as ContextType);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isLoading && (!user || user.role !== 'admin')) {
      navigate('/login');
    }
  }, [user, isLoading, navigate]);

  // Reset to page 1 when context or search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [context, search, selectedEventUuid, selectedEventId]);

  // Fetch events for dropdown when accounts or event-customers or book-tickets context is selected
  useEffect(() => {
    if ((context === 'accounts' || context === 'event-customers' || context === 'book-tickets') && user && user.role === 'admin') {
      eventsAPI.getAll({ limit: 1000 }).then(res => {
        setEventsForDropdown(res.data.events || []);
      }).catch(() => {
        setEventsForDropdown([]);
      });
    }
  }, [context, user]);

  // Fetch years for artist voting dropdown
  useEffect(() => {
    if (context === 'artist-voting' && user && user.role === 'admin') {
      artistsAPI.getYears().then(res => {
        const yearList = res.data.years || [];
        setYears(yearList);
        const currentYear = new Date().getFullYear();
        if (!yearList.includes(currentYear)) {
          setYears([currentYear, ...yearList]);
        }
      }).catch(() => {
        const currentYear = new Date().getFullYear();
        setYears([currentYear]);
      });
    }
  }, [context, user]);

  useEffect(() => {
    if (context === 'users' && user && user.role === 'admin') {
      setLoading(true);
      usersAPI.getAll({ search, page: currentPage }).then(res => {
        setUsers(res.data.users || []);
        setTotalPages(res.data.totalPages || 1);
      }).catch(() => {
        setUsers([]);
        setTotalPages(1);
      }).finally(() => setLoading(false));
    } else if (context === 'events' && user && user.role === 'admin') {
      setLoading(true);
      eventsAPI.getAll({ search, page: currentPage }).then(res => {
        setEvents(res.data.events || []);
        setTotalPages(res.data.totalPages || 1);
      }).catch(() => {
        setEvents([]);
        setTotalPages(1);
      }).finally(() => setLoading(false));
    } else if (context === 'customers' && user && user.role === 'admin') {
      setLoading(true);
      customersAPI.getAll({ search, page: currentPage }).then(res => {
        setCustomers(res.data.customers || []);
        setTotalPages(res.data.totalPages || 1);
      }).catch((err) => {
        setCustomers([]);
        setTotalPages(1);
      }).finally(() => setLoading(false));
    } else if (context === 'accounts' && user && user.role === 'admin' && selectedEventUuid) {
      setLoading(true);
      transactionsAPI.getByEventUuid(selectedEventUuid, { search, page: currentPage }).then(res => {
        setTransactions(res.data.transactions || []);
        setTotalPages(res.data.totalPages || 1);
      }).catch(() => {
        setTransactions([]);
        setTotalPages(1);
      }).finally(() => setLoading(false));
    } else if (context === 'event-customers' && user && user.role === 'admin' && selectedEventId) {
      setLoading(true);
      eventCustomersAPI.getByEventId(selectedEventId, { search, page: currentPage }).then(res => {
        setEventCustomers(res.data.eventCustomers || []);
        setTotalPages(res.data.totalPages || 1);
      }).catch(() => {
        setEventCustomers([]);
        setTotalPages(1);
      }).finally(() => setLoading(false));
    } else if (context === 'config' && user && user.role === 'admin') {
      setLoading(true);
      configAPI.getAll({ search, page: currentPage }).then(res => {
        setConfigs(res.data.configs || []);
        setTotalPages(res.data.totalPages || 1);
      }).catch(() => {
        setConfigs([]);
        setTotalPages(1);
      }).finally(() => setLoading(false));
    } else if (context === 'book-tickets' && user && user.role === 'admin') {
      setLoading(true);
      customersAPI.getAll({ limit: 1000 }).then(res => {
        setCustomers(res.data.customers || []);
      }).catch(() => {
        setCustomers([]);
      }).finally(() => setLoading(false));
    } else if (context === 'artist-voting' && user && user.role === 'admin') {
      setLoading(true);
      artistsAPI.getArtists(selectedYear).then(res => {
        setArtists(res.data.artists || []);
      }).catch(() => {
        setArtists([]);
      }).finally(() => setLoading(false));
    } else if (context === 'tasks' && user && user.role === 'admin') {
      setLoading(true);
      tasksAPI.getAll({ 
        search, 
        page: currentPage, 
        status: taskStatusFilter,
        assignedTo: taskAssignedFilter 
      }).then(res => {
        setTasks(res.data.tasks || []);
        setTotalPages(res.data.totalPages || 1);
      }).catch(() => {
        setTasks([]);
        setTotalPages(1);
      }).finally(() => setLoading(false));

      // Fetch task stats
      tasksAPI.getStats().then(res => {
        setTaskStats(res.data.stats);
      }).catch(() => {
        setTaskStats(null);
      });
    }
  }, [context, search, user, currentPage, selectedEventUuid, selectedEventId, selectedYear, taskStatusFilter, taskAssignedFilter]);

  // Keyboard shortcut for adding transaction (Ctrl+N or Cmd+N)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if Ctrl+N (Windows/Linux) or Cmd+N (Mac) is pressed
      if ((event.ctrlKey || event.metaKey) && event.key === 'n') {
        // Only trigger on accounts context when event is selected
        if (context === 'accounts' && selectedEventUuid && user?.role === 'admin') {
          event.preventDefault(); // Prevent browser's default "New Window" action
          setEditTransaction(null);
          setShowAccountsModal(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [context, selectedEventUuid, user]);

    // Auto-dismiss alert after 10 seconds
    useEffect(() => {
      if (alert) {
        const timer = setTimeout(() => {
          setAlert(null);
        }, 10000);
        return () => clearTimeout(timer);
      }
    }, [alert]);

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center text-primary-600 text-xl">Checking authentication...</div>;
  }

  return (
    <div className="min-h-screen flex bg-gray-50">
      {/* Alert Box */}
      {alert && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-2xl px-4">
          <AlertBox type={alert.type} message={alert.message} onClose={() => setAlert(null)} />
        </div>
      )}

      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-primary-700 text-white rounded-lg shadow-lg hover:bg-primary-800 transition"
        aria-label="Toggle menu"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {sidebarOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-64 bg-primary-700 text-white flex-shrink-0 flex flex-col py-8 shadow-lg
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="px-8 mb-8">
          <h2 className="text-2xl font-bold mb-2">Dashboard</h2>
          <p className="text-primary-200 text-sm">Admin Panel</p>
        </div>
        <nav className="flex-1 px-8 overflow-y-auto">
          <ul className="space-y-4">
            <li>
              <button onClick={() => { setContext('accounts'); setSidebarOpen(false); }} className={`block w-full text-left py-2 px-4 rounded-lg font-semibold transition ${context === 'accounts' ? 'bg-primary-600' : 'hover:bg-primary-600'}`}>Accounts</button>
            </li>
            <li>
              <button onClick={() => { setContext('artist-voting'); setSidebarOpen(false); }} className={`block w-full text-left py-2 px-4 rounded-lg font-semibold transition ${context === 'artist-voting' ? 'bg-primary-600' : 'hover:bg-primary-600'}`}>Artist Voting</button>
            </li>
            <li>
              <button onClick={() => { setContext('book-tickets'); setSidebarOpen(false); }} className={`block w-full text-left py-2 px-4 rounded-lg font-semibold transition ${context === 'book-tickets' ? 'bg-primary-600' : 'hover:bg-primary-600'}`}>Book Tickets</button>
            </li>
            <li>
              <button onClick={() => { setContext('config'); setSidebarOpen(false); }} className={`block w-full text-left py-2 px-4 rounded-lg font-semibold transition ${context === 'config' ? 'bg-primary-600' : 'hover:bg-primary-600'}`}>Configuration</button>
            </li>
            <li>
              <button onClick={() => { setContext('customers'); setSidebarOpen(false); }} className={`block w-full text-left py-2 px-4 rounded-lg font-semibold transition ${context === 'customers' ? 'bg-primary-600' : 'hover:bg-primary-600'}`}>Customers</button>
            </li>
            <li>
              <button onClick={() => { setContext('event-customers'); setSidebarOpen(false); }} className={`block w-full text-left py-2 px-4 rounded-lg font-semibold transition ${context === 'event-customers' ? 'bg-primary-600' : 'hover:bg-primary-600'}`}>Event Customers</button>
            </li>
            <li>
              <button onClick={() => { setContext('events'); setSidebarOpen(false); }} className={`block w-full text-left py-2 px-4 rounded-lg font-semibold transition ${context === 'events' ? 'bg-primary-600' : 'hover:bg-primary-600'}`}>Events</button>
            </li>
            <li>
              <Link to="/reports" className="block w-full text-left py-2 px-4 rounded-lg font-semibold transition hover:bg-primary-600" onClick={() => setSidebarOpen(false)}>Reports</Link>
            </li>
            <li>
              <button onClick={() => { setContext('tasks'); setSidebarOpen(false); }} className={`block w-full text-left py-2 px-4 rounded-lg font-semibold transition ${context === 'tasks' ? 'bg-primary-600' : 'hover:bg-primary-600'}`}>Tasks</button>
            </li>
            <li>
              <button onClick={() => { setContext('users'); setSidebarOpen(false); }} className={`block w-full text-left py-2 px-4 rounded-lg font-semibold transition ${context === 'users' ? 'bg-primary-600' : 'hover:bg-primary-600'}`}>Users</button>
            </li>
          </ul>
        </nav>
      </aside>
      {/* Main Content */}
      <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-12">
        <div className="flex flex-col mb-8">
          <h1 className="text-3xl font-bold text-primary-800 mb-4 text-center">{context.charAt(0).toUpperCase() + context.slice(1).replace('-', ' ')}</h1>
          {(context === 'accounts' || context === 'event-customers') && (
            <div className="w-full max-w-lg mb-4 mx-auto">
              <label className="block text-sm font-semibold mb-2 text-primary-700">Select Event:</label>
              <select
                value={context === 'accounts' ? selectedEventUuid : (selectedEventId?.toString() || '')}
                onChange={e => {
                  if (context === 'accounts') {
                    setSelectedEventUuid(e.target.value);
                  } else {
                    const eventId = e.target.value ? parseInt(e.target.value) : null;
                    setSelectedEventId(eventId);
                  }
                }}
                className="w-full px-6 py-3 border-2 border-primary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg font-medium shadow"
              >
                <option value="">-- Choose an Event --</option>
                {eventsForDropdown.map(ev => (
                  <option key={ev.uuid} value={context === 'accounts' ? ev.uuid : ev.id}>{ev.title}</option>
                ))}
              </select>
            </div>
          )}
          {context === 'artist-voting' && (
            <div className="w-full max-w-lg mb-4 mx-auto">
              <label className="block text-sm font-semibold mb-2 text-primary-700">Select Year:</label>
              <select
                value={selectedYear}
                onChange={e => setSelectedYear(parseInt(e.target.value))}
                className="w-full px-6 py-3 border-2 border-primary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg font-medium shadow"
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          )}
          {context !== 'artist-voting' && (
            <div className="w-full max-w-lg mb-4 mx-auto">
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={`Search ${context.replace('-', ' ')}...`}
                className="w-full px-6 py-3 border-2 border-primary-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent text-lg font-medium shadow"
                style={{ textAlign: 'center' }}
              />
            </div>
          )}
          {context === 'users' && user?.role === 'admin' && (
            <div className="w-full max-w-lg mb-4 mx-auto flex justify-center">
              <button
                onClick={() => {
                  setEditUserUuid(null);
                  setShowUserModal(true);
                }}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add User
              </button>
            </div>
          )}
          {context === 'customers' && user?.role === 'admin' && (
            <div className="w-full max-w-lg mb-4 mx-auto flex justify-center">
              <button
                onClick={() => {
                  setEditCustomerUuid(null);
                  setShowCustomerModal(true);
                }}
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Customer
              </button>
            </div>
          )}
          {context === 'tasks' && user?.role === 'admin' && (
            <div className="w-full max-w-lg mb-4 mx-auto flex justify-center">
              <button
                onClick={() => {
                  setEditTaskUuid(null);
                  setShowTaskModal(true);
                }}
                className="px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-800 hover:from-primary-700 hover:to-primary-900 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Task
              </button>
            </div>
          )}
        </div>
        {loading ? (
          <div className="text-center text-primary-600 text-lg py-8">Loading...</div>
        ) : (
          <>
            {context === 'users' ? (
              <>
                <DashboardTable
                  context={context}
                  data={filteredData.map(u => ({
                    ...u,
                    _actions: user?.role === 'admin' ? (
                      <button
                        type="button"
                        className="w-8 h-8 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all duration-200 shadow hover:shadow-lg transform hover:scale-105"
                        onClick={() => {
                          setEditUserUuid(u.uuid);
                          setShowUserModal(true);
                        }}
                        title="Edit User"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    ) : null
                  }))}
                />
                <UserModal
                  uuid={editUserUuid}
                  isOpen={showUserModal}
                  onClose={() => {
                    setShowUserModal(false);
                    setEditUserUuid(null);
                  }}
                  onSave={() => {
                    setShowUserModal(false);
                    setEditUserUuid(null);
                    // Refresh users after edit/create
                    usersAPI.getAll({ search, page: currentPage }).then(res => {
                      setUsers(res.data.users || []);
                      setTotalPages(res.data.totalPages || 1);
                    });
                  }}
                />
              </>
            ) : context === 'events' ? (
              <>
                <DashboardTable
                  context={context}
                  data={filteredData.map(ev => ({
                    ...ev,
                    _actions: user?.role === 'admin' ? (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="w-8 h-8 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all duration-200 shadow hover:shadow-lg transform hover:scale-105"
                          onClick={() => {
                            setEditEventUuid(ev.uuid);
                            setShowEventModal(true);
                          }}
                          title="Edit Event"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <label className="w-8 h-8 flex items-center justify-center bg-accent-500 hover:bg-accent-600 text-white rounded-lg cursor-pointer transition-all duration-200 shadow hover:shadow-lg transform hover:scale-105" title="Upload CSV">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <input
                            type="file"
                            accept=".csv"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleCsvUpload(file, ev.id);
                                e.target.value = '';
                              }
                            }}
                          />
                        </label>
                        <Link
                          to={`/events/${ev.uuid}/report`}
                          className="w-8 h-8 flex items-center justify-center bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all duration-200 shadow hover:shadow-lg transform hover:scale-105"
                          title="View Report"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </Link>
                      </div>
                    ) : null
                  }))}
                />
                <EventModal
                  uuid={editEventUuid || ''}
                  isOpen={showEventModal && !!editEventUuid}
                  onClose={() => {
                    setShowEventModal(false);
                    setEditEventUuid(null);
                  }}
                  onSave={() => {
                    setShowEventModal(false);
                    setEditEventUuid(null);
                    // Refresh events after edit
                    eventsAPI.getAll({ search, page: currentPage }).then(res => {
                      setEvents(res.data.events || []);
                      setTotalPages(res.data.totalPages || 1);
                    });
                  }}
                />
              </>
            ) : context === 'customers' ? (
              <>
                <DashboardTable
                  context={context}
                  data={filteredData.map(c => ({
                    ...c,
                    _actions: user?.role === 'admin' ? (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="w-8 h-8 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all duration-200 shadow hover:shadow-lg transform hover:scale-105"
                          onClick={() => {
                            setEditCustomerUuid(c.uuid);
                            setShowCustomerModal(true);
                          }}
                          title="Edit Customer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="w-8 h-8 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200 shadow hover:shadow-lg transform hover:scale-105"
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to delete customer "${c.customer_name}"? This will also delete all related tickets and event registrations.`)) {
                              customersAPI.delete(c.uuid)
                                .then(() => {
                                  setAlert({ type: 'success', message: 'Customer deleted successfully' });
                                  customersAPI.getAll({ search, page: currentPage }).then(res => {
                                    setCustomers(res.data.customers || []);
                                    setTotalPages(res.data.totalPages || 1);
                                  });
                                })
                                .catch((err) => {
                                  setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to delete customer' });
                                });
                            }
                          }}
                          title="Delete Customer"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ) : null
                  }))}
                />
                <CustomersModal
                  uuid={editCustomerUuid || ''}
                  isOpen={showCustomerModal}
                  onClose={() => {
                    setShowCustomerModal(false);
                    setEditCustomerUuid(null);
                  }}
                  onSave={() => {
                    setShowCustomerModal(false);
                    setEditCustomerUuid(null);
                    // Refresh customers after edit/create
                    customersAPI.getAll({ search, page: currentPage }).then(res => {
                      setCustomers(res.data.customers || []);
                      setTotalPages(res.data.totalPages || 1);
                    });
                  }}
                />
              </>
            ) : context === 'event-customers' ? (
              <>
                {!selectedEventId ? (
                  <div className="text-center text-primary-600 text-lg py-8">Please select an event to view customers.</div>
                ) : (
                  <>
                    {eventCustomers.length > 0 && (
                      <div className="flex justify-end mb-4">
                        <button
                          type="button"
                          onClick={exportEventCustomersCsv}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold text-sm shadow transition-all duration-200 hover:shadow-lg"
                          title="Export customer list as CSV"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Export CSV
                        </button>
                      </div>
                    )}
                  <DashboardTable
                    context={context}
                    data={filteredData.map((ec: any) => ({
                      ...ec,
                      confirmed: ec.confirmed ? ((ec.confirmed ? 'Yes' : 'No')):
                        <div className="inline-flex items-center px-2 py-1 bg-red-100 border-2 border-red-500 rounded" title="Amount Refunded">
                          {ec.confirmed ? 'Yes' : 'No'}
                          </div>,
                      ticket_emailed: ec.ticket_emailed ? 'Yes' : 'No',
                      _actions: user?.role === 'admin' ? (
                        <div className="flex gap-2 items-center">
                          {ec.mode === 'stripe' ? (
                            <div
                              className="w-8 h-8 flex items-center justify-center bg-gray-200 text-gray-400 rounded-lg cursor-not-allowed"
                              title="Stripe-paid bookings cannot be edited"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="w-8 h-8 flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all duration-200 shadow hover:shadow-lg transform hover:scale-105"
                              onClick={() => {
                                setEditEventCustomerUuid(ec.uuid);
                                setShowEventCustomerModal(true);
                              }}
                              title="Edit Customer"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          <button
                            type="button"
                            className={`w-8 h-8 flex items-center justify-center rounded-lg font-semibold transition-all duration-200 shadow hover:shadow-lg ${
                              ec.ticket_emailed 
                                ? 'bg-gray-400 text-white cursor-not-allowed' 
                                : sendingTickets === ec.uuid
                                  ? 'bg-yellow-500 text-white'
                                  : 'bg-green-500 text-white hover:bg-green-600 hover:scale-105'
                            }`}
                            onClick={() => !ec.ticket_emailed && handleSendTickets(ec.uuid)}
                            disabled={ec.ticket_emailed || sendingTickets === ec.uuid}
                            title={ec.ticket_emailed ? 'Tickets Sent' : 'Send Tickets'}
                          >
                            {sendingTickets === ec.uuid ? (
                              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : ec.ticket_emailed ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      ) : null
                    }))}
                    onCustomerNameClick={handleCustomerNameClick}
                  />
                  </>
                )}
              </>
            ) : context === 'accounts' ? (
              <>
                {!selectedEventUuid ? (
                  <div className="text-center text-primary-600 text-lg py-8">Please select an event to view transactions.</div>
                ) : (
                  <>
                    <div className="flex justify-end mb-4">
                      <button
                        type="button"
                        className="w-10 h-10 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all duration-200 shadow hover:shadow-lg transform hover:scale-105"
                        onClick={() => {
                          setEditTransaction(null);
                          setShowAccountsModal(true);
                        }}
                        title="Add Transaction (Ctrl+N / Cmd+N)"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                    <DashboardTable
                      context={context}
                      data={filteredData.map(tx => ({
                        ...tx,
                        _actions: (
                          <div className="flex items-center gap-1">
                            {user?.role === 'admin' && (
                              <button
                                type="button"
                                className="w-8 h-8 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all duration-200 shadow hover:shadow-lg transform hover:scale-105"
                                onClick={() => {
                                  setEditTransaction(tx);
                                  setShowAccountsModal(true);
                                }}
                                title="Edit Transaction"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                            )}
                            <button
                              type="button"
                              className="w-8 h-8 flex items-center justify-center bg-secondary-100 hover:bg-secondary-200 text-secondary-700 rounded-lg transition-all duration-200 shadow hover:shadow-lg transform hover:scale-105"
                              onClick={() => {
                                setReceiptTransaction(tx);
                                setShowReceiptModal(true);
                              }}
                              title="View Receipt"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v6a2 2 0 01-2 2h-4m-4 0v6m0 0l-2-2m2 2l2-2" />
                              </svg>
                            </button>
                          </div>
                        )
                      }))}
                    />
                    <ReceiptModal
                      isOpen={showReceiptModal}
                      onClose={() => {
                        setShowReceiptModal(false);
                        setReceiptTransaction(null);
                      }}
                      transaction={receiptTransaction}
                    />
                    <AccountsModal
                      isOpen={showAccountsModal}
                      onClose={() => {
                        setShowAccountsModal(false);
                        setEditTransaction(null);
                      }}
                      onSave={() => {
                        setShowAccountsModal(false);
                        setEditTransaction(null);
                        // Refresh transactions after add/edit
                        if (selectedEventUuid) {
                          transactionsAPI.getByEventUuid(selectedEventUuid, { search, page: currentPage }).then(res => {
                            setTransactions(res.data.transactions || []);
                            setTotalPages(res.data.totalPages || 1);
                          });
                        }
                      }}
                      eventUuid={selectedEventUuid}
                      transaction={editTransaction}
                    />
                  </>
                )}
              </>
            ) : context === 'config' ? (
              <>
                <div className="flex justify-end mb-4">
                  <button
                    type="button"
                    className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center gap-2"
                    onClick={() => {
                      setEditConfigUuid(null);
                      setShowConfigModal(true);
                    }}
                    title="Add Configuration"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Configuration
                  </button>
                </div>
                <DashboardTable
                  context={context}
                  data={filteredData.map((cfg: any) => ({
                    ...cfg,
                    _actions: user?.role === 'admin' ? (
                      <div className="flex gap-2">
                        <button
                          type="button"
                          className="w-8 h-8 flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-all duration-200 shadow hover:shadow-lg transform hover:scale-105"
                          onClick={() => {
                            setEditConfigUuid(cfg.uuid);
                            setShowConfigModal(true);
                          }}
                          title="Edit Configuration"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          className="w-8 h-8 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200 shadow hover:shadow-lg transform hover:scale-105"
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to delete this configuration?')) {
                              try {
                                await configAPI.delete(cfg.uuid);
                                setAlert({ type: 'success', message: 'Configuration deleted successfully.' });
                                // Refresh configs
                                configAPI.getAll({ search, page: currentPage }).then(res => {
                                  setConfigs(res.data.configs || []);
                                  setTotalPages(res.data.totalPages || 1);
                                });
                              } catch (error: any) {
                                setAlert({ type: 'error', message: error.response?.data?.error || 'Failed to delete configuration' });
                              }
                            }
                          }}
                          title="Delete Configuration"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ) : null
                  }))}
                />
                <ConfigModal
                  isOpen={showConfigModal}
                  onClose={() => {
                    setShowConfigModal(false);
                    setEditConfigUuid(null);
                  }}
                  onSave={async (data) => {
                    try {
                      if (editConfigUuid) {
                        await configAPI.update(editConfigUuid, data);
                        setAlert({ type: 'success', message: 'Configuration updated successfully.' });
                      } else {
                        await configAPI.create(data);
                        setAlert({ type: 'success', message: 'Configuration created successfully.' });
                      }
                      setShowConfigModal(false);
                      setEditConfigUuid(null);
                      // Refresh configs
                      configAPI.getAll({ search, page: currentPage }).then(res => {
                        setConfigs(res.data.configs || []);
                        setTotalPages(res.data.totalPages || 1);
                      });
                    } catch (error: any) {
                      setAlert({ type: 'error', message: error.response?.data?.error || 'Failed to save configuration' });
                    }
                  }}
                  editConfig={editConfigUuid ? configs.find(c => c.uuid === editConfigUuid) : null}
                />
              </>
            ) : context === 'book-tickets' ? (
              <BookTicketsForm 
                events={eventsForDropdown}
                customers={customers}
                onSuccess={() => {
                  setAlert({ type: 'success', message: 'Ticket booked successfully!' });
                }}
                onError={(message: string) => {
                  setAlert({ type: 'error', message });
                }}
              />
            ) : context === 'tasks' ? (
              <div className="w-full">
                {/* Task Stats */}
                {taskStats && (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-gray-500 to-gray-600 rounded-lg shadow p-4 text-white">
                      <div className="text-2xl font-bold">{taskStats.backlog_count}</div>
                      <div className="text-sm opacity-90">Backlog</div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow p-4 text-white">
                      <div className="text-2xl font-bold">{taskStats.wip_count}</div>
                      <div className="text-sm opacity-90">In Progress</div>
                    </div>
                    <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow p-4 text-white">
                      <div className="text-2xl font-bold">{taskStats.closed_count}</div>
                      <div className="text-sm opacity-90">Closed</div>
                    </div>
                    <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg shadow p-4 text-white">
                      <div className="text-2xl font-bold">{taskStats.critical_count}</div>
                      <div className="text-sm opacity-90">Critical</div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow p-4 text-white">
                      <div className="text-2xl font-bold">{taskStats.high_count}</div>
                      <div className="text-sm opacity-90">High Priority</div>
                    </div>
                    <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg shadow p-4 text-white">
                      <div className="text-2xl font-bold">{taskStats.unassigned_count}</div>
                      <div className="text-sm opacity-90">Unassigned</div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow p-4 text-white">
                      <div className="text-2xl font-bold">{taskStats.total_count}</div>
                      <div className="text-sm opacity-90">Total</div>
                    </div>
                  </div>
                )}

                {/* Task Filters */}
                <div className="bg-white rounded-lg shadow p-4 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700">Filter by Status:</label>
                      <select
                        value={taskStatusFilter}
                        onChange={e => setTaskStatusFilter(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">All Statuses</option>
                        <option value="backlog">Backlog</option>
                        <option value="wip">Work in Progress</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700">Filter by Assigned User:</label>
                      <select
                        value={taskAssignedFilter}
                        onChange={e => setTaskAssignedFilter(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">All Users</option>
                        {users.filter(u => u.role === 'admin').map(u => (
                          <option key={u.id} value={u.id}>{u.username}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Task Table */}
                {loading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                  </div>
                ) : tasks.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <h3 className="mt-2 text-lg font-medium text-gray-900">No tasks found</h3>
                    <p className="mt-1 text-gray-500">Create your first task to get started.</p>
                  </div>
                ) : (
                  <>
                    {/* Mobile Card View */}
                    <div className="md:hidden space-y-4">
                      {tasks.map((task) => (
                        <div key={task.uuid} className="bg-white rounded-lg shadow p-4">
                          <div className="space-y-3">
                            <div>
                              <h3 className="text-base font-semibold text-gray-900 mb-1 break-words">{task.title}</h3>
                              {task.description && (
                                <p className="text-sm text-gray-600 break-words line-clamp-2">{task.description}</p>
                              )}
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                task.status === 'backlog' ? 'bg-gray-100 text-gray-800' :
                                task.status === 'wip' ? 'bg-blue-100 text-blue-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {task.status === 'wip' ? 'In Progress' : task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                              </span>
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                task.priority === 'critical' ? 'bg-red-100 text-red-800' :
                                task.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                              </span>
                            </div>

                            <div className="text-sm text-gray-600 space-y-1">
                              <div className="flex items-center">
                                <span className="font-medium mr-2">Assigned:</span>
                                <span>{task.assigned_to_username || 'Unassigned'}</span>
                              </div>
                              {task.due_date && (
                                <div className="flex items-center">
                                  <span className="font-medium mr-2">Due:</span>
                                  <span>{new Date(task.due_date).toLocaleDateString()}</span>
                                </div>
                              )}
                            </div>

                            <div className="flex gap-2 pt-2 border-t">
                              <button
                                onClick={() => { setEditTaskUuid(task.uuid); setShowTaskModal(true); }}
                                className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteTask(task.uuid)}
                                className="flex-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Desktop Table View */}
                    <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Title
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Priority
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Assigned To
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Due Date
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {tasks.map((task) => (
                              <tr key={task.uuid} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <div className="text-sm font-medium text-gray-900 break-words max-w-xs">{task.title}</div>
                                  {task.description && (
                                    <div className="text-sm text-gray-500 break-words line-clamp-2 max-w-xs">{task.description}</div>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    task.status === 'backlog' ? 'bg-gray-100 text-gray-800' :
                                    task.status === 'wip' ? 'bg-blue-100 text-blue-800' :
                                    'bg-green-100 text-green-800'
                                  }`}>
                                    {task.status === 'wip' ? 'In Progress' : task.status.charAt(0).toUpperCase() + task.status.slice(1)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    task.priority === 'critical' ? 'bg-red-100 text-red-800' :
                                    task.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                                    task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-green-100 text-green-800'
                                  }`}>
                                    {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {task.assigned_to_username || 'Unassigned'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {task.due_date ? new Date(task.due_date).toLocaleDateString() : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                  <div className="flex justify-end gap-2">
                                    <button
                                      onClick={() => { setEditTaskUuid(task.uuid); setShowTaskModal(true); }}
                                      className="text-primary-600 hover:text-primary-900"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTask(task.uuid)}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : context === 'artist-voting' ? (
              <div className="w-full">
                {loading ? (
                  <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                  </div>
                ) : artists.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <h3 className="mt-2 text-lg font-medium text-gray-900">No submissions found</h3>
                    <p className="mt-1 text-gray-500">There are no artist submissions for {selectedYear}.</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Artist Name
                            </th>
                            <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Email
                            </th>
                            <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Submitted
                            </th>
                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Votes
                            </th>
                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {artists.map((artist: any) => (
                            <tr key={artist.id} className="hover:bg-gray-50">
                              <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                <div className="font-medium text-gray-900 text-sm sm:text-base">
                                  {artist.firstName} {artist.lastName}
                                </div>
                                <div className="sm:hidden text-xs text-gray-500">{artist.email}</div>
                              </td>
                              <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {artist.email}
                              </td>
                              <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDateEST(artist.createdAt)}
                              </td>
                              <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    ✓ {artist.votes.accept}
                                  </span>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    ✗ {artist.votes.reject}
                                  </span>
                                </div>
                              </td>
                              <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                                  <button
                                    onClick={() => openVoteModal(artist)}
                                    className="text-primary-600 hover:text-primary-900 text-sm"
                                  >
                                    Vote
                                  </button>
                                  {artist.decisionEmailed ? (
                                    <button
                                      disabled
                                      title="Decision already sent"
                                      className="px-2 sm:px-3 py-1 bg-green-100 text-green-700 rounded-md cursor-not-allowed text-xs font-medium whitespace-nowrap"
                                    >
                                      ✅ Sent
                                    </button>
                                  ) : artist.votes.total >= 3 ? (
                                    <button
                                      onClick={() => handleSendDecision(artist.id)}
                                      disabled={sendingDecision === artist.id}
                                      className="px-2 sm:px-3 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-md hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium whitespace-nowrap"
                                    >
                                      {sendingDecision === artist.id ? 'Sending...' : '📧 Decision'}
                                    </button>
                                  ) : (
                                    <button
                                      disabled
                                      title={`${5 - artist.votes.total} more vote(s) needed`}
                                      className="px-2 sm:px-3 py-1 bg-gray-300 text-gray-500 rounded-md cursor-not-allowed text-xs font-medium whitespace-nowrap"
                                    >
                                      📧 Decision
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <DashboardTable context={context} data={filteredData} />
            )}
            {/* Pagination Bar */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center mt-8 gap-2">
                <button
                  type="button"
                  className="px-3 py-2 rounded-lg bg-primary-100 text-primary-700 font-semibold hover:bg-primary-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >First</button>
                <button
                  type="button"
                  className="px-3 py-2 rounded-lg bg-primary-100 text-primary-700 font-semibold hover:bg-primary-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >Prev</button>
                <span className="px-4 py-2 text-lg font-bold text-primary-800">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="px-3 py-2 rounded-lg bg-primary-100 text-primary-700 font-semibold hover:bg-primary-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >Next</button>
                <button
                  type="button"
                  className="px-3 py-2 rounded-lg bg-primary-100 text-primary-700 font-semibold hover:bg-primary-200 disabled:opacity-50"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >Last</button>
              </div>
            )}
          </>
        )}
      </main>
      
      {/* Event Customer Modal */}
      {showEventCustomerModal && editEventCustomerUuid && selectedEventId && (
        <EventCustomerModal
          uuid={editEventCustomerUuid}
          isOpen={showEventCustomerModal}
          eventId={selectedEventId}
          onClose={() => {
            setShowEventCustomerModal(false);
            setEditEventCustomerUuid(null);
          }}
          onSave={() => {
            setShowEventCustomerModal(false);
            setEditEventCustomerUuid(null);
            setAlert({ type: 'success', message: 'Event customer updated successfully.' });
            // Refresh event customers
            if (selectedEventId) {
              eventCustomersAPI.getByEventId(selectedEventId, { search, page: currentPage }).then(res => {
                setEventCustomers(res.data.eventCustomers || []);
                setTotalPages(res.data.totalPages || 1);
              });
            }
          }}
        />
      )}

      {/* Tickets Modal */}
      <TicketsModal
        eventCustomerUuid={selectedTicketsCustomerUuid}
        isOpen={showTicketsModal}
        onClose={() => {
          setShowTicketsModal(false);
          setSelectedTicketsCustomerUuid(null);
          setSelectedTicketsCustomerName('');
        }}
        customerName={selectedTicketsCustomerName}
      />
      
      {/* Artist Vote Modal */}
      {showVoteModal && selectedArtist && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-start mb-4 sm:mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Vote on Artist Submission</h2>
                <button
                  onClick={() => {
                    setShowVoteModal(false);
                    setSelectedArtist(null);
                    setCurrentVote(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={voteLoading}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Artist Name</h3>
                  <p className="text-base sm:text-lg font-semibold text-gray-900">
                    {selectedArtist.firstName} {selectedArtist.lastName}
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500">Email</h3>
                  <p className="text-sm sm:text-base text-gray-900 break-all">{selectedArtist.email}</p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500">YouTube Video</h3>
                  <a
                    href={selectedArtist.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 underline break-all text-sm sm:text-base"
                  >
                    {selectedArtist.youtubeUrl}
                  </a>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500">Submission Date</h3>
                  <p className="text-sm sm:text-base text-gray-900">
                    {formatDateTimeEST(selectedArtist.createdAt)}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Current Voting Status</h3>
                  <div className="flex gap-4 flex-wrap">
                    <div className="flex items-center">
                      <span className="text-green-600 font-semibold mr-2">✓ Accept:</span>
                      <span className="text-lg font-bold">{selectedArtist.votes.accept}</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-red-600 font-semibold mr-2">✗ Reject:</span>
                      <span className="text-lg font-bold">{selectedArtist.votes.reject}</span>
                    </div>
                  </div>
                </div>

                {currentVote !== null && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-blue-800 text-sm sm:text-base">
                      You have already voted: <strong>{currentVote ? 'Accept' : 'Reject'}</strong>
                    </p>
                    <p className="text-sm text-blue-600 mt-1">You can change your vote below.</p>
                  </div>
                )}
              </div>

              <div className="border-t pt-4 sm:pt-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4">Cast Your Vote</h3>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <button
                    onClick={() => handleVote(selectedArtist.id, false)}
                    disabled={voteLoading}
                    className={`flex-1 py-3 px-4 rounded-md font-medium transition-colors ${
                      voteLoading
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                  >
                    {voteLoading ? 'Voting...' : '✗ Reject'}
                  </button>
                  <button
                    onClick={() => handleVote(selectedArtist.id, true)}
                    disabled={voteLoading}
                    className={`flex-1 py-3 px-4 rounded-md font-medium transition-colors ${
                      voteLoading
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {voteLoading ? 'Voting...' : '✓ Accept'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Modal */}
      {showTaskModal && (
        <TaskModal
          taskUuid={editTaskUuid}
          onClose={() => {
            setShowTaskModal(false);
            setEditTaskUuid(null);
          }}
          onSave={() => {
            // Refresh tasks list
            if (user && user.role === 'admin') {
              tasksAPI.getAll({ 
                search, 
                page: currentPage, 
                status: taskStatusFilter,
                assignedTo: taskAssignedFilter 
              }).then(res => {
                setTasks(res.data.tasks || []);
                setTotalPages(res.data.totalPages || 1);
              });

              // Refresh task stats
              tasksAPI.getStats().then(res => {
                setTaskStats(res.data.stats);
              });
            }
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
